import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'equasion-lib';
import { FiCheckCircle, FiChevronUp, FiPrinter } from 'react-icons/fi';
import {
  RECENT_SHIPMENT_TTL_MS,
  shippedAgo,
  useRecentShipments,
  type RecentShipment
} from '@/lib/recentShipments';
import './RecentShipments.scss';

/** The Documents page an entry reopens. `shipping=true` gives it Done, which comes back here. */
export const recentShipmentPath = (entry: Pick<RecentShipment, 'orderId'>) =>
  `/sales-order-documents/${entry.orderId}?shipping=true`;

const actionLabel = (entry: RecentShipment) => (entry.printed ? 'Reprint' : 'Documents');

const detail = (entry: RecentShipment, now: number) =>
  [entry.orderCount > 1 ? `${entry.orderCount} orders` : null, shippedAgo(entry.shippedAt, now)]
    .filter(Boolean)
    .join(' · ');

/** Re-render on a slow clock so "2 min ago" stays true and expired entries drop off. */
const useMinuteClock = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
};

/**
 * The home screen's "Last shipped" bar: the order this device shipped last, one tap from its
 * Documents page to print a label again. Generate Labels comes straight back here when RxPrint
 * printed the labels, so this is where a reprint lives now. The few shipments before it are a tap
 * away in a sheet. Nothing shows until something has shipped.
 */
const RecentShipments = ({ companyId }: { companyId: string | null | undefined }) => {
  const navigate = useNavigate();
  const now = useMinuteClock();
  const [listOpen, setListOpen] = useState(false);
  const entries = useRecentShipments(companyId).filter(
    (entry) => now - entry.shippedAt < RECENT_SHIPMENT_TTL_MS
  );
  if (!entries.length) return null;
  const [latest, ...earlier] = entries;
  const open = (entry: RecentShipment) => {
    setListOpen(false);
    navigate(recentShipmentPath(entry));
  };

  return (
    <section className="recent-shipments" aria-label="Recently shipped">
      <button
        type="button"
        className="recent-shipments-latest"
        onClick={() => open(latest)}
        aria-label={`${actionLabel(latest)} ${latest.label}, shipped ${shippedAgo(
          latest.shippedAt,
          now
        )}`}
      >
        <FiCheckCircle className="recent-shipments-check" aria-hidden="true" />
        <span className="recent-shipments-text">
          <span className="recent-shipments-eyebrow">Last shipped</span>
          <span className="recent-shipments-order">
            {latest.label}
            <span className="recent-shipments-detail"> · {detail(latest, now)}</span>
          </span>
        </span>
        <span className="recent-shipments-action">
          <FiPrinter aria-hidden="true" />
          {actionLabel(latest)}
        </span>
      </button>
      {earlier.length > 0 && (
        <button
          type="button"
          className="recent-shipments-more"
          onClick={() => setListOpen(true)}
          aria-label={`Show ${entries.length} recent shipments`}
        >
          <FiChevronUp aria-hidden="true" />
          {entries.length}
        </button>
      )}
      <Modal className="recent-shipments-sheet" modalOpen={listOpen} setModalOpen={setListOpen}>
        <div className="recent-shipments-sheet-heading">Recently shipped</div>
        <p className="recent-shipments-sheet-lead">
          Open an order to print its labels or documents again.
        </p>
        <ul className="recent-shipments-list">
          {entries.map((entry) => (
            <li key={entry.orderId}>
              <button type="button" onClick={() => open(entry)}>
                <span className="recent-shipments-text">
                  <span className="recent-shipments-order">{entry.label}</span>
                  <span className="recent-shipments-detail">{detail(entry, now)}</span>
                </span>
                <span className="recent-shipments-action">
                  <FiPrinter aria-hidden="true" />
                  {actionLabel(entry)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    </section>
  );
};

export default RecentShipments;
